import React, { memo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  TableCell,
  TextField,
  Typography,
} from '@mui/material';
import { CategoryMaster } from '../../../../services/apiTypes';
import {
  Add,
  Edit,
  Delete,
  DragHandle,
  KeyboardArrowDown,
  KeyboardArrowRight,
  CheckOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import { manageItemStyle } from '../InspectionItemTable';
import { MANAGE_ITEM_MODE } from '../../../../_constants/manageConst';
import { MaxLength } from '../../../../_constants/maxLengthConst';

type CategoryRowProps = {
  category: CategoryMaster;
  mode: number;
  isAdd: boolean;
  isAdmin: boolean;
  stickyPosition: number[];
  countItems: number;
  isDisabledBtn: boolean;
  isOpenAccordion: boolean;
  onClickAccordion: (id: string) => void;
  onClickAddIcon: (id: string) => void;
  onClickEditIcon: (data: CategoryMaster) => void;
  onClickDeleteIcon: (data: CategoryMaster) => void;
  onClickOk: (categoryName: string, isOptionalAddition: boolean) => void;
  onClickCancel: () => void;
};

const CategoryRowComponent: React.FC<CategoryRowProps> = ({
  category,
  mode,
  isAdd,
  isAdmin,
  stickyPosition,
  countItems,
  isDisabledBtn,
  isOpenAccordion,
  onClickAccordion,
  onClickAddIcon,
  onClickEditIcon,
  onClickDeleteIcon,
  onClickOk,
  onClickCancel,
}) => {
  /** 新規追加時の入力項目 */
  const [categoryName, setCategoryName] = useState('');
  const [isOptionalAddition, setIsOptionalAddition] = useState(false);

  useEffect(() => {
    setCategoryName(category.categoryName);
    setIsOptionalAddition(isAdmin ? category.isOptionalAddition : true);
  }, [category]);

  return (
    <React.Fragment>
      <TableCell
        sx={[
          manageItemStyle.iconCellCenter,
          manageItemStyle.leftSideSticky,
          { left: stickyPosition[0], verticalAlign: 'top' },
        ]}
        rowSpan={isOpenAccordion ? countItems + 1 : 1}
      >
        {mode === MANAGE_ITEM_MODE.SORT && (
          <IconButton size="small" sx={{ cursor: 'grab', touchAction: 'none' }}>
            <DragHandle fontSize="small" />
          </IconButton>
        )}
      </TableCell>
      <TableCell
        sx={[
          manageItemStyle.leftSideSticky,
          { left: stickyPosition[1], py: 0 },
        ]}
        colSpan={5}
      >
        <Box sx={manageItemStyle.nameBox}>
          {!!countItems ? (
            <IconButton
              size="small"
              onClick={() => {
                onClickAccordion(category.categoryId);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              sx={manageItemStyle.iconCellCenter}
            >
              {isOpenAccordion ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
            </IconButton>
          ) : (
            <Box sx={manageItemStyle.emptyBox} />
          )}
          {isAdd ? (
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={categoryName}
              name="displayName"
              sx={{ py: 1 }}
              onChange={(e) => setCategoryName(e.target.value)}
              onPointerDown={(e) => e.stopPropagation()}
              slotProps={{
                htmlInput: {
                  maxLength: MaxLength.ManageInspectionItem.CATEGORY_NAME,
                },
              }}
            />
          ) : (
            <Typography sx={manageItemStyle.nameTypography}>
              {category.displayName}
            </Typography>
          )}
        </Box>
      </TableCell>
      {isAdmin && (
        <TableCell sx={manageItemStyle.iconCellCenter}>
          <Checkbox
            name="isOptionalAddition"
            disabled={!isAdd}
            checked={isAdd ? isOptionalAddition : category.isOptionalAddition}
            onChange={(e) => setIsOptionalAddition(e.target.checked)}
            sx={{ py: '2px' }}
          />
        </TableCell>
      )}
      <TableCell sx={{ py: 0 }} colSpan={15} />
      {mode === MANAGE_ITEM_MODE.VIEW ? (
        <React.Fragment>
          <TableCell
            sx={[manageItemStyle.icon1Sticky, manageItemStyle.iconCellCenter]}
          >
            <IconButton
              size="small"
              sx={manageItemStyle.iconCellCenter}
              onClick={() => onClickAddIcon(category.categoryId)}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Add fontSize="small" color="primary" />
            </IconButton>
          </TableCell>
          <TableCell
            sx={[manageItemStyle.icon2Sticky, manageItemStyle.iconCellCenter]}
          >
            {(isAdmin || category.isOptionalAddition) && (
              <IconButton
                size="small"
                sx={manageItemStyle.iconCellCenter}
                onClick={() => onClickEditIcon(category)}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
          </TableCell>
          <TableCell
            sx={[manageItemStyle.icon3Sticky, manageItemStyle.iconCellCenter]}
          >
            {/* 子項目がなければ削除アイコンを表示する */}
            {!countItems && (isAdmin || category.isOptionalAddition) && (
              <IconButton
                size="small"
                sx={manageItemStyle.iconCellCenter}
                onClick={() => onClickDeleteIcon(category)}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Delete fontSize="small" color="error" />
              </IconButton>
            )}
          </TableCell>
        </React.Fragment>
      ) : isAdd ? (
        <TableCell
          sx={[
            manageItemStyle.iconCellCenter,
            manageItemStyle.iconEditingSticky,
          ]}
          colSpan={3}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              size="small"
              sx={[
                manageItemStyle.iconCellCenter,
                manageItemStyle.iconEditing,
                isDisabledBtn
                  ? manageItemStyle.editDisabled
                  : manageItemStyle.editOk,
              ]}
              onClick={() => onClickOk(categoryName, isOptionalAddition)}
              disabled={isDisabledBtn}
            >
              <CheckOutlined sx={{ fontSize: '16px' }} />
            </Button>
            <Button
              size="small"
              sx={[
                manageItemStyle.iconCellCenter,
                manageItemStyle.iconEditing,
                isDisabledBtn
                  ? manageItemStyle.editDisabled
                  : manageItemStyle.editCancel,
              ]}
              onClick={onClickCancel}
              disabled={isDisabledBtn}
            >
              <CloseOutlined sx={{ fontSize: '16px' }} />
            </Button>
          </Box>
        </TableCell>
      ) : (
        <TableCell colSpan={3} />
      )}
    </React.Fragment>
  );
};

export const CategoryRow = memo(CategoryRowComponent);
